import { Scene, WebGLRenderer, Clock, Mesh, Material } from 'three';
import { Ground } from '../world/Ground';
import { Sky } from '../world/Sky';
import { PlayerAircraft } from '../aircraft/PlayerAircraft';
import { FollowCamera } from '../camera/FollowCamera';
import { InputManager } from '../utils/InputManager';
import { HUD, ThreatInfo } from '../ui/HUD';
import { RadarMap } from '../ui/RadarMap';
import { PauseMap } from '../ui/PauseMap';
import { MobileControls } from '../ui/MobileControls';
import { Vulcan } from '../weapons/Vulcan';
import { MissileManager } from '../weapons/MissileManager';
import { LockOnSystem } from '../systems/LockOnSystem';
import { EnemyManager } from '../enemy/EnemyManager';
import { Explosion } from '../effects/Explosion';
import { TrailEffect } from '../effects/TrailEffect';
import { StageData } from './StageLoader';
import { AudioManager } from '../audio/AudioManager';
import { SoundEffects } from '../audio/SoundEffects';
import { BGMPlayer } from '../audio/BGMPlayer';
import { ScoreResult, calculateScore } from './ScoreCalculator';
import { AircraftData } from '../data/aircraft';

export type GameState = 'waiting' | 'playing' | 'paused' | 'clear' | 'gameover';

export interface GameCallbacks {
  onClear: (result: ScoreResult) => void;
  onGameOver: () => void;
}

export class Game {
  private scene: Scene;
  private renderer: WebGLRenderer;
  private clock: Clock;
  private ground: Ground;
  private player: PlayerAircraft;
  private followCam: FollowCamera;
  private input: InputManager;
  private hud: HUD;
  private radar: RadarMap;
  private pauseMap: PauseMap;
  private mobileControls: MobileControls;
  private vulcan: Vulcan;
  private missileManager: MissileManager;
  private lockOn: LockOnSystem;
  private enemyManager: EnemyManager;
  private explosion: Explosion;
  private trail: TrailEffect;
  private audio: AudioManager;
  private sfx: SoundEffects;
  private bgm: BGMPlayer;
  private callbacks: GameCallbacks;

  private state: GameState = 'waiting';
  private playerMaxHp = 100;
  private playerHp = 100;

  private wasVulcanFiring = false;
  private prevHp = 100;
  private audioStarted = false;
  private screenShakeTimer = 0;
  private screenShakeIntensity = 0;
  private deathDelayTimer = 0;  // ゲームオーバー前の演出猶予

  // スコア追跡
  private elapsedTime = 0;
  private missilesFired = 0;
  private vulcanHitCount = 0;

  // ウェーブ管理
  private waves: import('../game/StageLoader').EnemyDef[][] = [];
  private currentWave = 0;
  private totalEnemyCount = 0;

  private pauseOverlay!: HTMLElement;

  constructor(
    scene: Scene,
    renderer: WebGLRenderer,
    input: InputManager,
    followCam: FollowCamera,
    stageData: StageData,
    audio: AudioManager,
    callbacks: GameCallbacks,
    aircraftData?: AircraftData,
  ) {
    this.scene = scene;
    this.renderer = renderer;
    this.clock = new Clock();
    this.input = input;
    this.followCam = followCam;
    this.audio = audio;
    this.callbacks = callbacks;

    if (aircraftData) {
      this.playerMaxHp = aircraftData.maxHp;
      this.playerHp = aircraftData.maxHp;
      this.prevHp = aircraftData.maxHp;
    }

    new Sky(scene, stageData.terrain);
    this.ground = new Ground(scene, stageData.terrain);

    this.player = new PlayerAircraft(scene, input, aircraftData);
    this.vulcan = new Vulcan(scene, this.player);
    this.missileManager = new MissileManager(scene);

    if (aircraftData) {
      this.vulcan.maxAmmo = aircraftData.vulcanAmmo;
      this.vulcan.ammo = aircraftData.vulcanAmmo;
      this.missileManager.maxAmmo = aircraftData.missiles;
      this.missileManager.ammo = aircraftData.missiles;
    }

    this.lockOn = new LockOnSystem();
    this.enemyManager = new EnemyManager(
      scene, stageData.enemies,
      (x, z) => this.ground.getHeightAt(x, z),
    );
    this.enemyManager.setPlayerRef(this.player.group);
    this.explosion = new Explosion(scene);
    this.trail = new TrailEffect(scene);

    // ウェーブ設定
    this.waves = stageData.waves ?? [];
    this.currentWave = 0;
    this.totalEnemyCount = stageData.enemies.length
      + this.waves.reduce((sum, w) => sum + w.length, 0);

    this.hud = new HUD(this.playerMaxHp, this.totalEnemyCount);
    this.radar = new RadarMap();
    this.pauseMap = new PauseMap(stageData.mapWorld);
    this.mobileControls = new MobileControls(input);

    this.sfx = new SoundEffects(audio);
    this.bgm = new BGMPlayer(audio);

    this.pauseOverlay = document.getElementById('pause-overlay')!;

    if (input.isMobile) {
      this.setupMobile();
    } else {
      this.setupPC();
    }

    this.setupPauseScreen();
  }

  /** PC: Pointer Lock ベースのゲーム開始・ポーズ */
  private setupPC(): void {
    document.addEventListener('pointerlockchange', () => {
      if (document.pointerLockElement) {
        this.startOrResume();
      } else {
        if (this.state === 'playing') {
          this.pause();
        }
      }
    });
  }

  /** モバイル: ポーズボタンで一時停止 */
  private setupMobile(): void {
    document.addEventListener('mobile-pause', () => {
      if (this.state === 'playing') {
        this.pause();
      }
    });

    this.pauseOverlay.addEventListener('touchstart', (e) => {
      if (this.state === 'paused') {
        e.preventDefault();
        this.startOrResume();
      }
    }, { passive: false });
  }

  /** GameFlowから呼ばれるゲーム開始 */
  start(): void {
    this.startOrResume();
  }

  private startOrResume(): void {
    if (!this.audioStarted) {
      this.audioStarted = true;
      this.audio.resume();
      this.sfx.startEngine();
      this.bgm.start();
    }
    if (this.state === 'waiting' || this.state === 'paused') {
      this.state = 'playing';
      this.clock.getDelta();
      this.audio.resumeAudio();
      this.hidePauseOverlay();
      this.mobileControls.show();
    }
  }

  private pause(): void {
    if (this.state !== 'playing') return;
    this.state = 'paused';
    this.sfx.stopVulcan();
    this.wasVulcanFiring = false;
    this.clock.getDelta();
    this.audio.pauseAudio();
    this.showPauseOverlay();
    this.mobileControls.hide();
  }

  update(): void {
    if (this.state === 'waiting' || this.state === 'paused') {
      this.clock.getDelta();
      return;
    }

    if (this.state !== 'playing') return;

    const delta = Math.min(this.clock.getDelta(), 0.05);
    this.elapsedTime += delta;

    this.mobileControls.update();

    const gh = this.ground.getHeightAt(this.player.position.x, this.player.position.z);

    this.player.update(delta, gh);
    this.followCam.update(this.player.group, delta);

    const enemies = this.enemyManager.getAliveEnemies();
    const target = this.lockOn.update(this.player.group, enemies, this.input.isDown('Tab'), delta);

    // バルカン
    const firingVulcan = this.input.isDown('KeyF') || this.input.isDown('ControlLeft');
    this.vulcan.update(delta, firingVulcan);

    if (firingVulcan && !this.wasVulcanFiring) this.sfx.startVulcan();
    if (!firingVulcan && this.wasVulcanFiring) this.sfx.stopVulcan();
    this.wasVulcanFiring = firingVulcan;

    const bulletHits = this.vulcan.checkHits(enemies);
    for (const { enemy, pos } of bulletHits) {
      this.vulcanHitCount++;
      this.explosion.spawnSmall(pos);
      this.sfx.playExplosionSmall();
      if (enemy.takeDamage(10)) {
        this.explosion.spawnLarge(enemy.position);
        this.sfx.playExplosionLarge();
        this.enemyManager.remove(enemy);
      }
    }

    // ミサイル発射
    if (this.input.consumeKeyOnce('Space') && target) {
      this.missilesFired++;
      this.missileManager.fire(this.player.group, target);
      this.sfx.playMissileLaunch();
    }

    this.missileManager.update(delta);
    const missileHits = this.missileManager.checkHits(enemies);
    for (const { enemy, pos } of missileHits) {
      this.explosion.spawnLarge(pos);
      this.sfx.playExplosionLarge();
      if (enemy.takeDamage(60)) {
        this.explosion.spawnLarge(enemy.position);
        this.sfx.playExplosionLarge();
        this.enemyManager.remove(enemy);
      }
    }

    // 敵攻撃 → プレイヤーダメージ
    const playerHit = this.enemyManager.update(delta, this.player.group, this.explosion);
    if (playerHit > 0) {
      this.playerHp = Math.max(0, this.playerHp - playerHit);
      if (this.playerHp < this.prevHp) {
        // ミサイル被弾かどうかで演出を分ける
        if (playerHit >= 30) {
          this.sfx.playMissileHit();
          this.screenShakeTimer = 0.5;
          this.screenShakeIntensity = 8;
        } else {
          this.sfx.playHit();
          this.screenShakeTimer = 0.15;
          this.screenShakeIntensity = 3;
        }
      }
      if (this.playerHp <= 0 && this.deathDelayTimer <= 0) {
        // 大ダメージ死亡は演出猶予を設ける
        this.deathDelayTimer = playerHit >= 30 ? 1.0 : 0.3;
        this.screenShakeTimer = this.deathDelayTimer;
        this.screenShakeIntensity = 12;
      }
    }
    this.prevHp = this.playerHp;

    // 死亡演出猶予
    if (this.deathDelayTimer > 0 && this.playerHp <= 0) {
      this.deathDelayTimer -= delta;
      if (this.deathDelayTimer <= 0) {
        this.triggerGameOver();
      }
    }

    // 敵ミサイル爆発エフェクト
    const missilePositions = this.enemyManager.getEnemyMissilePositions();

    const boost = this.input.isDown('ShiftLeft') || this.input.isDown('ShiftRight');

    this.explosion.update(delta);
    this.trail.update(this.player.group, {
      boost,
      accel: this.input.isDown('KeyW'),
      decel: this.input.isDown('KeyS'),
      speedKmh: this.player.speedKmh,
    }, delta);

    // 画面揺れ
    if (this.screenShakeTimer > 0) {
      this.screenShakeTimer -= delta;
      const shake = this.screenShakeIntensity * (this.screenShakeTimer / 0.5);
      this.followCam.camera.position.x += (Math.random() - 0.5) * shake;
      this.followCam.camera.position.y += (Math.random() - 0.5) * shake;
    }

    if (this.enemyManager.getAliveEnemies().length === 0) {
      if (this.currentWave < this.waves.length) {
        // 次のウェーブを投入
        this.enemyManager.spawnDefs(this.waves[this.currentWave]);
        this.currentWave++;
      } else {
        this.triggerClear();
      }
    }
    this.sfx.updateEngine(this.player.speedKmh, boost);
    this.sfx.updateLockOn(target !== null, delta);

    // 脅威警告
    const lockedByEnemy = this.enemyManager.isPlayerLockedByEnemy();
    const incomingMissiles = missilePositions.length > 0;
    this.sfx.updateThreatWarning(lockedByEnemy, incomingMissiles, delta);

    const threat: ThreatInfo = { lockedByEnemy, incomingMissiles };

    const remainingEnemies = this.enemyManager.getAliveEnemies().length;
    this.hud.update(
      this.player.speedKmh,
      this.player.altitudeM,
      this.playerHp,
      this.vulcan.ammo,
      this.missileManager.ammo,
      target,
      this.followCam,
      remainingEnemies,
      threat,
    );
    const playerMissiles = this.missileManager.getActiveMissilePositions();
    this.radar.update(this.player.group, enemies, missilePositions, playerMissiles);
  }

  private showPauseOverlay(): void {
    this.pauseOverlay.style.display = 'flex';
    const enemies = this.enemyManager.getAliveEnemies();
    this.pauseMap.update(this.player.group, enemies);
  }

  private hidePauseOverlay(): void {
    this.pauseOverlay.style.display = 'none';
  }

  private setupPauseScreen(): void {
    document.getElementById('pause-resume')?.addEventListener('click', () => {
      if (this.state !== 'paused') return;
      if (this.input.isMobile) {
        this.startOrResume();
      } else {
        this.input.requestPointerLock();
      }
    });
  }

  private triggerClear(): void {
    if (this.state !== 'playing') return;
    this.state = 'clear';
    this.bgm.stop();
    this.sfx.stopVulcan();
    this.mobileControls.hide();
    if (!this.input.isMobile && document.pointerLockElement) document.exitPointerLock();

    const result = calculateScore(
      this.elapsedTime,
      this.playerHp,
      this.playerMaxHp,
      this.missilesFired,
      this.vulcanHitCount,
    );
    this.callbacks.onClear(result);
  }

  private triggerGameOver(): void {
    if (this.state !== 'playing') return;
    this.state = 'gameover';
    this.bgm.stop();
    this.sfx.stopVulcan();
    this.mobileControls.hide();
    if (!this.input.isMobile && document.pointerLockElement) document.exitPointerLock();
    this.callbacks.onGameOver();
  }

  dispose(): void {
    this.bgm.stop();
    this.sfx.stopVulcan();
    this.vulcan.dispose();
    this.mobileControls.hide();

    this.scene.traverse((child) => {
      if (child instanceof Mesh) {
        child.geometry.dispose();
        const mats = Array.isArray(child.material) ? child.material : [child.material];
        mats.forEach((m: Material) => m.dispose());
      }
    });
    while (this.scene.children.length > 0) {
      this.scene.remove(this.scene.children[0]);
    }
  }
}
